import React, {useState, useEffect} from 'react';
import {useQueryClient} from "react-query";
import axios from "axios";
import __ from "lodash";
import { FormControl, FormGroup, Grid, Icon, IconButton, TextField, Tooltip } from "@material-ui/core";
import { FormDialog, LabelledPaper, MaskedText } from '../controls';
import { getApiSitePrefix } from "../functions/SiteDefaults";
import { getTemplate, getRecipientList } from "../functions/TemplateHelperFunctions";
import { populateComplexTemplate } from "../functions/PopulateTemplate";
import StringToJSX from "../functions/StringToJsx";

import SendEmailDialogValidator from "./validators/SendEmailDialogValidator";
import { useCompanyContacts } from "../services/companies";
import { useSendEmail } from "../services/system";

const apiSitePrefix = getApiSitePrefix();

const EmailBodyView = (props) => {
  const record = props.record;

  return (
    <div style={{width: "100%", maxHeight: props.maxHeight, overflowY: "scroll"}}>
      <StringToJSX domString={populateComplexTemplate(record.template, record, [...record.repeatData])}/>
    </div>
  );
}

const formatChars = {
  "U": "[A-Z]",
  "A": "[A-Z0-9]",
  "a": "[A-Za-z0-9]",
  "i": "[A-Za-z0-9\\-]",
  "9": "[0-9]",
  "D": "[0-9.]",
  "-": "-",
  "x": "."
}

const paperProps = {
  style: {
    backgroundColor: "#f8f8f8",
    boxShadow: 'none',
  }
}

const defaultLabelledPaperProps = {
  checkChildren: true,
  style: {display: "block", border: "solid 1px #b9b9b9" },
  labelStyle: {background: "#f0f0f0"},
  collapsible: false,
}

const getCompanyUuid = async (target, filter) => {
  let companyUuid = null;

  if (target === "airline") {
    const url = `${apiSitePrefix}/companies/getaircraftlessorcompany/${filter}`;
    const response = await axios.get(url, {
      headers: {
        'Access-Control-Allow-Origin': '*',
      }
    });
    if (response && response.data) {
      companyUuid = response.data.companyUuid;
    }
  } else {
    companyUuid = filter;
  }

  return companyUuid
}

const SendEmailDialog = (_props) => {
  const props = {
    canEdit: false,
    ..._props,
  }

  const SystemData = props.SystemData;
  const eventBus = SystemData.eventBus;

  const [dialogState, setDialogState] = useState({
      showDialog: false,
      record: {},
      title: 'Send Email',
      companyUuid: null,
    });
  const [requestSent, setRequestSent] = useState(false);
  const [alert, setAlert] = useState({
    open: false,
    severity: "error",
    message: "",
  });

  let companyContactsData = null;

  const getEmailTemplate = async (templateType, templateUuid) => {
    const template = await getTemplate(SystemData, "emailmanager", "template", templateType, templateUuid);
    return template;
  }

  const close = () => {
    setRequestSent(false);
    setAlert({open: false, severity: "error", message: ""});
    setDialogState({...dialogState, showDialog: false, record: {}, title: 'Send Email'});
    companyContactsData = null;
    queryClient.removeQueries(["CompanyContacts"]);
  }

  const queryClient = useQueryClient();
  const extraKeys = [dialogState.companyUuid ? dialogState.companyUuid : "", dialogState.record.contactRole];
  const companyContacts = useCompanyContacts({ extraKeys });
  const handleSendEmail = useSendEmail();

  companyContactsData = companyContacts.isSuccess ? companyContacts.data : [];

  useEffect(()=> {
    const open = async (params) => {
      const {record, title, canEdit} = params;
      // Get the template for the email body
      record["template"] = await getEmailTemplate(record.templateType, record.templateUuid);
      let companyUuid = await getCompanyUuid(record.target, record.targetFilter);

      // Set the contacts hook to return Operator and GHA contacts
      if (__.has(record, "contactRole") && record.contactRole === "TRIP SHEET" && __.has(record, "tripLegUuid")) {
        companyUuid = record.tripLegUuid;
      }

      setDialogState({...dialogState, showDialog: true, record, title, canEdit, companyUuid});
    }
    eventBus.bind("SendEmailDialog.open", open);
    return () => {
      eventBus.unbind("SendEmailDialog.open", open);
    }
  }, [dialogState, eventBus, queryClient]);


  const getButtons = () => {
    if (requestSent) {
      return (
        {
          save:{icon: "send", key: "Save", label: "Re-Send", color: "primary", disabled: (status)=>{return status.error}},
          cancel:{icon: "cancel", key: "Cancel", label: "Close", color: "primary"},
        }
      );
    }
    return (
      {
        save:{icon: "send", key: "Save", label: "Send", color: "primary", disabled: (status)=>{return status.error}},
        cancel:{icon: "cancel", key: "Cancel", label: "Cancel", color: "primary"}
      }
    );
  }

  if (dialogState.showDialog && companyContactsData) {
    return (
      <div key="formContainer" style={{width: "100%", textAlign:"left" }}>
        <FormDialog
          key="sendEmailMessage"
          title={dialogState.title}
          message=""
          open={dialogState.showDialog}
          dialogOptions={{maxWidth: "lg", fullWidth: true, PaperProps: paperProps }}
          record={{...dialogState.record, recipients: dialogState.record.recipients && dialogState.record.recipients !== "" ? dialogState.record.recipients : getRecipientList(companyContactsData, dialogState.record.contactRole)}}
          // record={dialogState.record}
          validator={SendEmailDialogValidator}
          validatorPosition="top"
          alert={alert}
          formHeightOffset={320}
          titleBarStyle={{backgroundColor: '#3f3f3f', color: 'primary'}}
          buttons={getButtons()}
          onChange={(state) => {
            if (state.buttonPressed === "cancel") {
              close();
              if (props.close)
                props.close();
            }
            if (state.buttonPressed === "save") {
              SystemData.toggleLoadingProgress(true);

              const emailContent = populateComplexTemplate(state.record.template, state.record, [...state.record.repeatData]);
              const updatedRecord = {...state.record, content: emailContent};

              handleSendEmail.mutate({
                extraKeys: [],
                record: updatedRecord,
                postSuccess: () => {
                  setRequestSent(true);
                  SystemData.toggleLoadingProgress(false);
                  const message = `${updatedRecord.subject} email sent`;
                  setAlert({ severity: "info", open: true, message: message, timeOut: 5 });
                },
                postError: (data) => {
                  SystemData.toggleLoadingProgress(false);
                  if (data.errorMessage) {
                    SystemData.alert("Error", `${data.errorMessage}`);
                  } else {
                    SystemData.alert("Error", `An Unexpected Error has occurred sending the ${updatedRecord.subject} email`);
                  }
                }
              });

            } else {
              console.log("Unhandled FormDialog onChange");
            }
          }}
        >
          <Grid container checkChildren={true} spacing={3}>
            <Grid item xs={12} checkChildren={true} row>
              <LabelledPaper {...defaultLabelledPaperProps} label="" >
                <FormControl checkChildren={true} key="FC-addresses" style={{display: "block" }}>
                  <FormGroup checkChildren={true} key="FG-addresses" style={{padding: "8px 8px 8px 8px"}}>
                    <Grid container checkChildren={true} spacing={3}>

                      <Grid item xs={12} checkChildren={true}>
                        <div style={{display: "flex"}}>
                          {(record, changeRecord) => (
                            <MaskedText
                              id="recipients"
                              label="To"
                              inputLabelProps={{ shrink: true }}
                              style={{width: "100%"}}
                              mask={"x".repeat(200)}
                              maskChar={""}
                              alwaysShowMask={false}
                              formatChars={formatChars}
                              forceUpperCase={false}
                              defaultValue={getRecipientList(companyContactsData, record.contactRole, record.airportCode)}
                            />
                          )}
                          {(record, changeRecord) => (
                            <Tooltip title="Restore default recipients">
                              <IconButton
                                style={{padding:2}}
                                disabled={ dialogState.record.recipients && dialogState.record.recipients !== "" ? record.recipients === dialogState.record.recipients : record.recipients === getRecipientList(companyContactsData, record.contactRole, record.airportCode) }
                                onClick={()=> {
                                  changeRecord({
                                    recipients: dialogState.record.recipients && dialogState.record.recipients !== "" ? dialogState.record.recipients : getRecipientList(companyContactsData, record.contactRole, record.airportCode)
                                  })
                                }}><Icon>restore</Icon></IconButton>
                            </Tooltip>
                          )}
                        </div>
                      </Grid>
                      <Grid item xs={12} checkChildren={true}>
                        <MaskedText
                          id="carbonCopy"
                          label="CC"
                          inputLabelProps={{ shrink: true }}
                          style={{width: "100%"}}
                          mask={"x".repeat(200)}
                          maskChar={""}
                          alwaysShowMask={false}
                          formatChars={formatChars}
                          forceUpperCase={false}
                        />
                      </Grid>
                      <Grid item xs={12} checkChildren={true}>
                        <MaskedText
                          id="subject"
                          label="Subject"
                          inputLabelProps={{ shrink: true }}
                          style={{width: "100%"}}
                          mask={"x".repeat(200)}
                          maskChar={""}
                          alwaysShowMask={false}
                          formatChars={formatChars}
                          forceUpperCase={false}
                        />
                      </Grid>
                      <Grid item xs={12} checkChildren={true}>
                        <TextField
                          id="messageAdditionalInfo"
                          label="Additional Information"
                          InputLabelProps={{ shrink: true }}
                          style={{width: "100%"}}
                          multiline
                          onChange={(value, props) => {
                            let additionalInfo = "";
                            if (value && value.length > 0) {
                              additionalInfo = value;
                            }
                            props.recordChange({...props.record, additionalInfo: additionalInfo, messageAdditionalInfo: value});
                          }}
                        />
                      </Grid>
                    </Grid>
                  </FormGroup>
                </FormControl>
              </LabelledPaper>
            </Grid>

            {record => {
              return (
                <Grid item xs={12} checkChildren={true}>
                  <LabelledPaper {...defaultLabelledPaperProps} label={`Message Body`} >
                    <FormControl checkChildren={true} key="FC-genericemailbody" style={{display: "block" }}>
                      <FormGroup checkChildren={true} key="FG-genericemailbody" style={{padding: "8px 8px 8px 8px"}}>
                        <EmailBodyView record={record} queryClient={queryClient}/>
                      </FormGroup>
                    </FormControl>
                  </LabelledPaper>
                </Grid>
              )}}
          </Grid>
        </FormDialog>
      </div>
    );
  }
  return (<></>);
}

export default SendEmailDialog;
